import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'
import { LANDING_NEW_NAV_CTA_LEFT_CONTROL_HEIGHT } from '../../../src/landing/constants/landingNewNavCta.constants'

// RN's StyleSheet types accept these CSS values at runtime but reject them at the type level.
// Centralize the casts so they don't multiply across the file.
const STYLE_AUTO_DIMENSION = 'auto' as unknown as number
const STYLE_FLEX_BASIS_FULL = '100%' as unknown as number
const STYLE_FLEX_NOWRAP = 'nowrap' as unknown as 'wrap'
const STYLE_POSITION_STICKY: 'relative' = 'sticky' as 'relative'

export const landingNewNavWebBarStyle =
  Platform.OS === 'web'
    ? ({
        backdropFilter: 'saturate(180%) blur(14px)',
        WebkitBackdropFilter: 'saturate(180%) blur(14px)',
      } as object)
    : undefined

export function getLandingNewNavStyles(theme: Theme) {
  return StyleSheet.create({
    bar: {
      position: Platform.OS === 'web' ? STYLE_POSITION_STICKY : 'relative',
      top: 0,
      zIndex: 50,
      backgroundColor: theme.background.primary + 'F5',
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[300],
    },
  inner: {
    paddingHorizontal: 24,
    height: 72,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  innerCompact: {
    height: STYLE_AUTO_DIMENSION,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
    flexWrap: 'wrap',
    columnGap: 12,
    rowGap: 10,
  },
  innerPhone: {
    height: 60,
    paddingTop: 0,
    paddingBottom: 0,
    flexWrap: STYLE_FLEX_NOWRAP,
    gap: 6,
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 180,
    flex: 0,
  },
  logoRowPhone: {
    flex: 1,
    minWidth: 0,
  },
  logoRowPressed: {
    opacity: 0.88,
  },
    logoMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: theme.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoMarkImage: {
    width: 36,
    height: 36,
  },
    logoText: {
    fontFamily: Platform.OS === 'web' ? '"Space Grotesk", "Inter", system-ui, sans-serif' : undefined,
    fontWeight: '700',
    fontSize: 22,
    letterSpacing: -0.4,
    color: theme.primaryDark,
  },
  ctaRow: {
    position: 'relative',
    zIndex: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    minWidth: 320,
    flex: 0,
    marginLeft: STYLE_AUTO_DIMENSION,
  },
  ctaRowCompact: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'flex-end',
    flexWrap: STYLE_FLEX_NOWRAP,
  },
  ctaRowPhone: {
    flex: 0,
    flexBasis: STYLE_AUTO_DIMENSION,
    width: 'auto',
    minWidth: 0,
    marginLeft: 0,
    justifyContent: 'flex-end',
    gap: 4,
  },
  headerRightSlot: {
    position: 'relative',
    zIndex: 60,
    marginLeft: STYLE_AUTO_DIMENSION,
    flexShrink: 0,
  },
  headerRightSlotCompact: {
    marginLeft: 0,
  },
  /**
   * Phone: language / extras sit on the top bar to the left of the menu control,
   * separated from CTAs by a vertical rule (not inside the slide-down panel).
   */
  headerRightSlotPhone: {
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 10,
    marginLeft: 4,
    borderLeftWidth: 1,
    borderLeftColor: theme.gray[300],
  },
  navLinksCompactSlot: {
    position: 'relative',
    zIndex: 10,
    width: '100%',
    flexBasis: STYLE_FLEX_BASIS_FULL,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ghostBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ghostBtnPhone: {
    paddingHorizontal: 10,
    minHeight: 36,
  },
    ghostBtnText: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 15,
    fontWeight: '600',
    color: theme.gray[800],
  },
  ghostBtnTextPhone: {
    fontSize: 13,
  },
    ghostBtnHovered: {
      color: theme.primaryDark,
    },
  openAppPressable: {
    height: LANDING_NEW_NAV_CTA_LEFT_CONTROL_HEIGHT,
    borderRadius: 16,
    overflow: 'hidden',
  },
  openAppGradient: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    gap: 7,
    paddingHorizontal: 20,
    borderRadius: 16,
    overflow: 'hidden',
  },
  openAppShine: {
    position: 'absolute',
    left: 0,
    top: -18,
    width: 32,
    height: 82,
    backgroundColor: 'rgba(255, 255, 255, 0.38)',
  },
  primaryBtnText: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
    letterSpacing: -0.1,
  },
  primaryBtnTextPhone: {
    fontSize: 13,
  },
  primaryBtnTextCompact: {
    fontSize: 14,
  },
  openAppGradientCompact: {
    paddingHorizontal: 16,
  },
  openAppGradientPhone: {
    paddingHorizontal: 14,
  },
    hamburgerBtn: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: theme.background.card + 'B3',
    borderWidth: 1,
    borderColor: theme.gray[300],
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
    hamburgerBtnActive: {
      backgroundColor: theme.background.shell,
      borderColor: theme.primary + '66',
    },
  })
}
